import React, { useRef, useCallback } from "react";
import { Modal, Button, Space, App as AntApp } from "antd";
import { DownloadOutlined, LinkOutlined, ShareAltOutlined } from "@ant-design/icons";
import QRCodeStyling from "qr-code-styling";
import styles from "./QRModal.module.css";

function getCSSVar(name) {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

const QRModal = React.memo(function QRModal({ open, url, name, onClose }) {
  const { message } = AntApp.useApp();
  const containerRef = useRef(null);
  const qrInstance = useRef(null);

  const generateQR = useCallback(() => {
    const el = containerRef.current;
    if (!el || !url) return;

    el.innerHTML = "";

    const textColor = getCSSVar("--zh-text") || "#0f172a";
    const surface = getCSSVar("--zh-surface") || "#ffffff";

    try {
      const qr = new QRCodeStyling({
        width: 240,
        height: 240,
        data: url,
        margin: 8,
        dotsOptions: { color: textColor, type: "rounded" },
        backgroundOptions: { color: surface },
        cornersSquareOptions: { type: "extra-rounded", color: textColor },
      });
      qrInstance.current = qr;
      qr.append(el);
    } catch (e) {
      console.error("QR generation error:", e);
    }
  }, [url]);

  const handleDownload = () => {
    if (!qrInstance.current) return;
    qrInstance.current.download({
      name: `QR-${(name || "store").replace(/\s+/g, "-")}`,
      extension: "png",
    });
  };

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(url);
      message.success("Link copied");
    } catch {
      message.error("Could not copy link");
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: name || "Store", url });
      } catch {}
    } else {
      handleCopyUrl();
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={440}
      centered
      destroyOnClose
      title="QR Code"
      afterOpenChange={(visible) => {
        if (visible) setTimeout(generateQR, 100);
      }}
    >
      <div className={styles.body}>
        <div className={styles.qrContainer} ref={containerRef} />
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.url}
          onClick={(e) => e.stopPropagation()}
        >
          {url}
        </a>
        <Space size="middle" className={styles.actions}>
          <Button className={styles.actionBtn} icon={<DownloadOutlined />} onClick={handleDownload}>
            Download
          </Button>
          <Button className={styles.actionBtn} icon={<LinkOutlined />} onClick={handleCopyUrl}>
            Copy Link
          </Button>
          <Button className={styles.actionBtn} icon={<ShareAltOutlined />} onClick={handleShare}>
            Share
          </Button>
        </Space>
      </div>
    </Modal>
  );
});

export default QRModal;
